import type { ComponentType } from 'react';
import { Box, Divider, MenuItem, Paper, Stack, type SvgIconProps } from '@mui/material';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import type { Element, FieldElement } from '../../format/form-definition';
import { canResizeField, fieldWidthOf } from '../../field-types';
import { FIELD_WIDTHS } from '../../format/fieldWidth';
import { AUTHOR_TEXTS } from '../../texts/author';
import { useBuilder } from '../context';
import { elementType } from '../fieldsPalette/tiles';
import { drawCopyIds } from '../edits/copy';
import { landingsByStep } from '../elementInsertion';
import IconButton from '@mui/material/IconButton';
import { Button, Tooltip } from 'framework/ui/mui-builder';
import { DropdownMenu } from 'framework/ui/menu';

export const ElementToolbar = ({ element }: { element: Element }) => {
  const { icon: Icon } = elementType(element);
  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'absolute',
        top: 0,
        right: 12,
        zIndex: 2,
        transform: 'translateY(-50%)',
        borderColor: 'primary.main',
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 1 }}>
        <Icon fontSize="inherit" color="primary" />
        {element.kind === 'field' && canResizeField(element) && <WidthMenu field={element} />}
        <ArrangeButtons element={element} />
      </Stack>
    </Paper>
  );
};

const WidthMenu = ({ field }: { field: FieldElement }) => {
  const { edit } = useBuilder();
  const currentWidth = fieldWidthOf(field);
  return (
    <>
      <Divider orientation="vertical" flexItem />
      <Box onClick={event => event.stopPropagation()}>
        <DropdownMenu
          popupId={`${field.id}_width_menu`}
          disablePortal={false}
          sxPaper={{ minWidth: 40 }}
          sxList={{ '& .MuiMenuItem-root': { fontSize: 12, p: 0, justifyContent: 'center' } }}
          iconButton={
            <Button
              variant="text"
              size="small"
              aria-label={`${AUTHOR_TEXTS.fieldWidth.label}: ${AUTHOR_TEXTS.fieldWidth.options[currentWidth]}`}
              aria-haspopup="menu"
              sx={{ minWidth: 0, height: 25, px: 0.5, fontSize: 10, textTransform: 'none' }}
            >
              {AUTHOR_TEXTS.fieldWidth.options[currentWidth]}
              <ArrowDropDown sx={{ fontSize: 14 }} />
            </Button>
          }
        >
          {FIELD_WIDTHS.map(width => (
            <MenuItem
              key={width}
              selected={width === currentWidth}
              role="menuitemradio"
              aria-checked={width === currentWidth}
              onClick={() => edit({ kind: 'setFieldWidth', fieldId: field.id, width })}
            >
              {AUTHOR_TEXTS.fieldWidth.options[width]}
            </MenuItem>
          ))}
        </DropdownMenu>
      </Box>
    </>
  );
};

const ArrangeButtons = ({ element }: { element: Element }) => {
  const { form, edit, select, openElement, drawer } = useBuilder();
  const canGo = {
    up: landingsByStep(form, element.id, 'up').length > 0,
    down: landingsByStep(form, element.id, 'down').length > 0,
  };

  const move = (direction: 'up' | 'down') => edit({ kind: 'moveElementByStep', elementId: element.id, direction });

  const duplicate = () => {
    const ids = drawCopyIds(form, element);
    const copyId = ids.get(element.id)!;
    edit({ kind: 'duplicateElement', elementId: element.id, ids });
    if (drawer?.kind === 'element') openElement(copyId);
    else select(copyId);
  };

  const remove = () => edit({ kind: 'deleteElement', elementId: element.id });

  return (
    <>
      <Divider orientation="vertical" flexItem />
      <ToolbarButton title="Move up" icon={ArrowUpward} disabled={!canGo.up} onClick={() => move('up')} />
      <ToolbarButton title="Move down" icon={ArrowDownward} disabled={!canGo.down} onClick={() => move('down')} />
      <ToolbarButton title="Duplicate" icon={ContentCopyOutlined} onClick={duplicate} />
      <ToolbarButton title="Delete" icon={DeleteOutlineOutlined} onClick={remove} />
    </>
  );
};

interface ToolbarButtonProps {
  title: string;
  icon: ComponentType<SvgIconProps>;
  disabled?: boolean;
  onClick: () => void;
}

const ToolbarButton = ({ title, icon: Icon, disabled = false, onClick }: ToolbarButtonProps) => {
  return (
    <Tooltip title={title} placement="top" enterDelay={400} enterNextDelay={200}>
      <span>
        <IconButton
          size="small"
          aria-label={title}
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            onClick();
          }}
        >
          <Icon sx={{ fontSize: 14 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
};
